/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// material
import {Switch, Group, IconControl, IncDecField} from '@insticator/insticator-ui'; // iui-material
import { url } from 'utils/urls';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller'
// actions
import { updateModerationSettings, updateModerationAdvanceSettings, updateModeration, cancelSectionChanges } from 'state/actions/commentingState/configurationStatus';
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/configuration/moderationsection/moderationsection_styles';


const ModerationSection = (props) => {

    // action dispatch
    const dispatch = useDispatch();
    const restoreStateOnCancel = (section) => dispatch(cancelSectionChanges(section));
    const updateModerationState = (prop, value) => dispatch(updateModerationSettings(prop, value));
    const updateModerationAdvanceSettingsState = (prop, value) => dispatch(updateModerationAdvanceSettings(prop, value));
    const saveModerationSettings = (uuid, data, callBack) => dispatch(updateModeration(uuid, data, callBack));

    // redux state
    const mocked = useSelector(({ sessionStatus }) => sessionStatus.mocked);
    const moderationUUID = useSelector(({ commentingState }) => commentingState.configStatus.moderation.uuid);
    const updated = useSelector(({ commentingState }) => commentingState.configStatus.moderation.updated);
    const savingModeration = useSelector(({ commentingState }) => commentingState.configStatus.moderation.savingModeration);
    const moderationType = useSelector(({ commentingState }) => commentingState.configStatus.moderation.moderationType);
    const anonymousCommenting = useSelector(({ commentingState }) => commentingState.configStatus.moderation.anonymousCommenting);
    const onlyAnonymousCommenting = useSelector(({ commentingState }) => commentingState.configStatus.moderation.onlyAnonymousCommenting);
    const isMediaContentEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.isMediaContentEnabled);
    const isVideosEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.isVideosEnabled);
    const isImagesEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.isImagesEnabled);
    const isGifsEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.isGifsEnabled);
    const autoBlockAuthors = useSelector(({ commentingState }) => commentingState.configStatus.moderation.autoBlockAuthors);
    const autoRemoveComments = useSelector(({ commentingState }) => commentingState.configStatus.moderation.autoRemoveComments);
    const blockCommentsWithLink = useSelector(({ commentingState }) => commentingState.configStatus.moderation.blockCommentsWithLink);
    const isEmojiReactionsEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.isEmojiReactionsEnabled);
    //advance
    const isAIModerationEnabled = useSelector(({ commentingState }) => commentingState.configStatus.moderation.advancedSettings.isAIModerationEnabled);
    const minApprovalScore = useSelector(({ commentingState }) => commentingState.configStatus.moderation.advancedSettings.minApprovalScore);
    const maxApprovalScore = useSelector(({ commentingState }) => commentingState.configStatus.moderation.advancedSettings.maxApprovalScore);



    // component state
    const [isOpened, setIsOpened] = useState(false);

    const saveData = () => {
        const advanceSettings = ({
            isAIModerationEnabled,
            minApprovalScore,
            maxApprovalScore
        });

        const commentingUserType = () => {
            if(!anonymousCommenting){
                return 'ONLY_AUTHORIZED_USER'
            }
            else if(anonymousCommenting && onlyAnonymousCommenting){
                return 'ONLY_ANONYMOUSE_USER'
            }
            return 'EVERY_USER'
        }

        return ({
            moderationType,
            commentingUserType: commentingUserType(),
            isMediaContentEnabled,
            isVideosEnabled,
            isImagesEnabled,
            isGifsEnabled,
            autoBlockAuthors,
            autoRemoveComments,
            blockCommentsWithLink,
            isEmojiReactionsEnabled,
            advancedSettings: mocked ? advanceSettings : null
        })
    };
    const saveModeration = () => saveModerationSettings(moderationUUID, saveData(), () => setIsOpened(false));

    const formatModerationType = (type) => type.split('_')[0].toLocaleLowerCase()

    const controller =  (
        <ConfigController
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            disableSave={!updated}
            processing={savingModeration}
            handleCancel={() => { restoreStateOnCancel('moderation') }}
            handleSave={saveModeration}
            title={'Moderation'}
            description={<SectionDescription
                main={`${formatModerationType(moderationType)}-moderation`}
                additional={'Anonymous commenting OFF, +4 more'}
            />}
        />
    );

    return (
        <ExpandableSection
            className={`moderationsection ${props.classes.moderationsection}`}
            controller={controller}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            disableSave={!updated}
            processing={savingModeration}
            handleCancel={() => restoreStateOnCancel('moderation')}
            handleSave={saveModeration}
        >
            <SectionCard>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Moderation type'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Group name={'controls'}>
                        <IconControl
                            text={'Post'}
                            selected={moderationType === 'POST_MODERATION'}
                            image={`${url.image}/illustrations/illustration-post-moderation.svg`}
                            value={'POST_MODERATION'}
                            handleSelect={value => updateModerationState('moderationType', value)}
                        />
                        <IconControl
                            text={'Pre'}
                            selected={moderationType === 'PRE_MODERATION'}
                            image={`${url.image}/illustrations/illustration-pre-moderation.svg`}
                            value={'PRE_MODERATION'}
                            handleSelect={value => updateModerationState('moderationType', value)}
                        />
                        <IconControl
                            text={'None'}
                            selected={moderationType === 'NO_MODERATION'}
                            image={`${url.image}/illustrations/illustration-no-moderation.svg`}
                            value={'NO_MODERATION'}
                            handleSelect={value => updateModerationState('moderationType', value)}
                        />
                    </Group>
                </SectionGroup>

                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Anonymous commenting'}
                        description={'Unauthorized users can post comments.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('anonymousCommenting', !anonymousCommenting )}
                        on={anonymousCommenting}
                        variant='secondary'
                    />
                </SectionGroup>
                {anonymousCommenting &&
                <SectionGroup row secondary>
                    <SectionGroupDescription title='Allow only anonymous commenting'/>
                    <Switch
                        on={onlyAnonymousCommenting}
                        isStatic={true}
                        handleClick={() => updateModerationState('onlyAnonymousCommenting', !onlyAnonymousCommenting )}
                        variant='secondary'
                    />
                </SectionGroup>}
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Rich media Comments'}
                        description={'Commenters can add rich media in comments (videos, images, GIFs).'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('isMediaContentEnabled', !isMediaContentEnabled )}
                        on={isMediaContentEnabled}
                        variant='secondary'
                    />
                </SectionGroup>
                {isMediaContentEnabled &&
                <SectionGroup row secondary>
                    <SectionGroupDescription title='Embedded videos (from sites like YouTube, etc.)'/>
                    <Switch
                        on={isVideosEnabled}
                        isStatic={true}
                        handleClick={() => updateModerationState('isVideosEnabled', !isVideosEnabled)}
                        variant='secondary'
                    />
                </SectionGroup>}
                {isMediaContentEnabled &&
                <SectionGroup row secondary>
                    <SectionGroupDescription title='Upload images'/>
                    <Switch
                        on={isImagesEnabled}
                        isStatic={true}
                        handleClick={() => updateModerationState('isImagesEnabled', !isImagesEnabled)}
                        variant='secondary'
                    />
                </SectionGroup>}
                {isMediaContentEnabled &&
                <SectionGroup row secondary>
                    <SectionGroupDescription title='GIFs (from Giphy library)'/>
                    <Switch
                        on={isGifsEnabled}
                        isStatic={true}
                        handleClick={() => updateModerationState('isGifsEnabled', !isGifsEnabled)}
                        variant='secondary'
                    />
                </SectionGroup>}
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Emoticon Comment Reactions'}
                        description={'Users can react to comments via emoticon reactions.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('isEmojiReactionsEnabled', !isEmojiReactionsEnabled)}
                        on={isEmojiReactionsEnabled}
                        variant='secondary'
                    />
                </SectionGroup>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Auto block commenter'}
                        description={'Users can mark comments as spam. We will automatically block a commenter after 10 inappropriate marks.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('autoBlockAuthors', !autoBlockAuthors)}
                        on={autoBlockAuthors}
                        variant='secondary'
                    />
                </SectionGroup>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Auto remove comments'}
                        description={'We will automatically remove a comment after 3 inappropriate marks.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('autoRemoveComments', !autoRemoveComments)}
                        on={autoRemoveComments}
                        variant='secondary'
                    />
                </SectionGroup>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Block comments with links'}
                        description={'Comments can\'t contain links.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationState('blockCommentsWithLink', !blockCommentsWithLink)}
                        on={blockCommentsWithLink}
                        variant='secondary'
                    />
                </SectionGroup>
            </SectionCard>
            {mocked &&
            <SectionCard title={'ADVANCED SETTINGS'}>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'AI moderation'}
                        description={'Enable automatic AI moderation.'}
                        // helpLink={'https://www.insticator.com/insticator-products/commenting/'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateModerationAdvanceSettingsState('isAIModerationEnabled', !isAIModerationEnabled)}
                        on={isAIModerationEnabled}
                        variant='secondary'
                    />
                </SectionGroup>
                {isAIModerationEnabled &&
                <div className='tablesectionview approvalthreshold'>
                    <SectionGroup secondary>
                        <SectionGroupDescription title={'Min approval threshold'}/>
                        <IncDecField
                            name={'incdecfield'}
                            defaultValue={minApprovalScore}
                            increment={1}
                            minValue={0}
                            maxValue={maxApprovalScore}
                            handleIncToggle={value => updateModerationAdvanceSettingsState('minApprovalScore', value)}
                            handleDecToggle={value => updateModerationAdvanceSettingsState('minApprovalScore', value)}
                            ctaPosition={'inline'}
                        />
                    </SectionGroup>
                    <SectionGroup secondary>
                        <SectionGroupDescription title={'Max approval threshold'}/>
                        <IncDecField
                            name={'incdecfield'}
                            defaultValue={maxApprovalScore}
                            increment={1}
                            minValue={minApprovalScore}
                            maxValue={6}
                            handleIncToggle={value => updateModerationAdvanceSettingsState('maxApprovalScore', value)}
                            handleDecToggle={value => updateModerationAdvanceSettingsState('maxApprovalScore', value)}
                            ctaPosition={'inline'}
                        />
                    </SectionGroup>
                </div>}

            </SectionCard>}

        </ExpandableSection>)
};

export default injectSheet(styles)(ModerationSection)
